import React, { useState, useEffect, useCallback } from 'react';
import Joyride, { STATUS, ACTIONS } from 'react-joyride';
import type { CallBackProps, Step } from 'react-joyride';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { shouldUseMobileLayout } from '../utils/deviceDetection';

const TOUR_STEPS: Step[] = [
  {
    target: '[data-tour="dashboard-grid"]',
    content: 'This is your dashboard. Drag widgets to rearrange them, and resize from the corners.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="add-tool"]',
    content: 'Click here to add trading tools to your dashboard.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="templates"]',
    content: 'Choose from pre-built layouts for different trading styles.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="audio-streams"]',
    content: 'Listen to live audio commentary while you trade.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="global-ticker"]',
    content: 'Type a ticker symbol here to sync all linked widgets at once.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="profile-link"]',
    content: 'Manage your account, billing, and Discord connection.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="discord-banner"]',
    content: 'Link your Discord account to join the trading community and get alerts.',
    placement: 'bottom',
  },
];

const joyrideStyles = {
  options: {
    backgroundColor: '#161616',
    textColor: '#e5e7eb',
    primaryColor: '#C2A14E',
    arrowColor: '#161616',
    overlayColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: 8,
    border: '1px solid #2B2B2B',
    fontSize: 14,
  },
  tooltipContent: {
    padding: '12px 16px',
  },
  buttonNext: {
    backgroundColor: '#C2A14E',
    color: '#0A0A0A',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
  },
  buttonBack: {
    color: '#9ca3af',
    fontSize: 13,
  },
  buttonSkip: {
    color: '#6b7280',
    fontSize: 13,
  },
  spotlight: {
    borderRadius: 8,
  },
};

interface OnboardingTourProps {
  triggerRestart?: number;
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ triggerRestart }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Check if tour was already completed
  const { data: stateData } = useQuery({
    queryKey: ['frontendState', 'tour'],
    queryFn: () => profileService.getFrontendState(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const tourCompleted = stateData?.frontend_state?.tourCompleted === true;

  // Auto-start tour for first-time users (after template loads)
  useEffect(() => {
    if (!user || tourCompleted) return;

    // Small delay to let the dashboard render with templates
    const timer = setTimeout(() => {
      setRun(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [user, tourCompleted]);

  // Handle manual restart from toolbar button
  useEffect(() => {
    if (triggerRestart && triggerRestart > 0) {
      setStepIndex(0);
      setRun(true);
    }
  }, [triggerRestart]);

  // Filter out steps whose targets don't exist in the DOM
  const activeSteps = TOUR_STEPS.filter((step) => {
    if (typeof step.target === 'string') {
      return document.querySelector(step.target) !== null;
    }
    return true;
  });

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, index, type } = data;

      if (type === 'step:after') {
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }

      // Tour dismissed: finished, skipped, or closed (X button / ESC / overlay).
      // Joyride's CLOSE action lands as status: 'paused' (resumable), so we
      // must check the action separately or every X-click leaves tourCompleted
      // false and the tour respawns on next reload.
      const dismissed =
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE;

      if (dismissed) {
        setRun(false);

        // Save completion flag (non-blocking)
        if (!tourCompleted) {
          profileService
            .saveFrontendState({ tourCompleted: true })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['frontendState', 'tour'] });
            })
            .catch((error) => {
              console.error('Failed to save tour completion:', error);
            });
        }
      }
    },
    [tourCompleted, queryClient]
  );

  // Don't render on the mobile shell (path-agnostic after /m removal).
  if (shouldUseMobileLayout()) {
    return null;
  }

  if (activeSteps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={activeSteps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      disableScrollParentFix
      callback={handleCallback}
      styles={joyrideStyles}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Done',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
};
